import { Release } from '@common/types/release/release';
import AppState from '@webapp/store/state';
import { trackList } from '@webapp/store/tracks/selectors';
import {
  collaboratorsList,
  contributorsList,
} from '../../store/collaborator/selectors';
import { ownersList, writersList } from '../../store/ownership/selectors';
import { isISRC, isUpc } from '.';
import { isRecordLabelNameValid } from '@webapp/utils/record-labels/is-record-label-name-valid';

export const titleSection = (state: AppState) => {
  const release = state.release.release;
  if (!release) {
    return false;
  }

  const tracks = trackList(state.tracks.tracks, release.id);

  return !!(release.title && release.type && release.cover && tracks.length);
};

export const detailsSection = (state: AppState) => {
  const release = state.release.release;
  if (!release) {
    return false;
  }

  return !!(
    titleSection(state) &&
    release.artists &&
    release.artists.length &&
    release.genres &&
    isRecordLabelNameValid(release.recordLabel || '') &&
    release.genres.length >= 1
  );
};

export const datesSection = (state: AppState) => {
  const release = state.release.release;
  if (!release) {
    return false;
  }
  const validUPC = state.release.release?.UPCEAN
    ? isUpc(state.release.release.UPCEAN)
    : true;

  return !!(
    detailsSection(state) &&
    validUPC &&
    release.releaseHistory &&
    ((release.releaseHistory === 'new' && release.releaseDate) ||
      (release.releaseHistory === 'previous' &&
        release.previousReleaseDate &&
        release.releaseDate))
  );
};

export const trackSection = (state: AppState, trackId: string) => {
  const release = state.release.release;
  if (!release) {
    return false;
  }

  const tracks = trackList(state.tracks.tracks, release.id);
  const track = tracks.find((t) => t.id === trackId);

  if (!track) {
    return false;
  }

  const collaborators = collaboratorsList(state.collaborator, trackId);
  const contributors = contributorsList(state.collaborator, trackId);
  const ownerOwnerships = ownersList(state.ownership, trackId);
  const writerOwnerships = writersList(state.ownership, trackId);

  if (contributors.filter((c) => !c.role).length > 0) {
    return false;
  }

  if (
    ownerOwnerships.reduce((memo, owner) => memo + owner.split * 10 ** 10, 0) /
      10 ** 10 !==
      100 ||
    writerOwnerships.reduce((memo, owner) => memo + owner.split * 10 ** 10, 0) /
      10 ** 10 !==
      100
  ) {
    return false;
  }

  const validType =
    !!track.type &&
    (track.type === 'song'
      ? track.isExplicit !== undefined && !!track.language
      : true);

  const validISRC = track?.isrc ? isISRC(track?.isrc) : true;

  return !!(
    validType &&
    validISRC &&
    datesSection(state) &&
    collaborators.length &&
    !!collaborators.find((a) => a.role === 'main')
  );
};

export const fullRelease = (state: AppState) => {
  const release = state.release.release;
  if (!release) {
    return false;
  }

  const tracks = trackList(state.tracks.tracks, release.id);

  return tracks.reduce(
    (memo, t) => memo && trackSection(state, t.id as string),
    true
  );
};

export const termsSection = (release: Partial<Release>) => {
  return (
    release.tosAccepted && release.authorAccepted && release.recordedAccepted
  );
};

export const validateRelease = (state: AppState) => {
  const release = state.release.release;
  if (release) {
    const tracks = trackList(state.tracks.tracks, release.id);
    const tracksCondition =
      tracks && tracks.length > 0
        ? tracks.every((track) =>
            track.id ? trackSection(state, track.id) : false
          )
        : false;

    return (
      titleSection(state) &&
      detailsSection(state) &&
      datesSection(state) &&
      fullRelease(state) &&
      termsSection(release) &&
      tracksCondition
    );
  }
  return false;
};

export const validStatusForSummaryButton = (state: AppState) => {
  if (
    ['pending', 'approved', 'published'].includes(
      state.release.release?.status || ''
    )
  ) {
    return false;
  }
  return true;
};
